import { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import chatService from 'ApiService/Student/chatService';

function AllConversations({
  goBack,
  conversations,
  onSelectConversation,
  loading,
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center">
        <button
          onClick={goBack}
          className="mr-4 text-gray-600 hover:text-gray-800"
        >
          ← Back
        </button>
        <h1 className="text-xl font-bold text-gray-800 dark:text-white">
          All Conversations
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center p-4">
          <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <div className="space-y-4">
          {conversations.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              No conversations found
            </div>
          ) : (
            conversations.map((conversation) => (
              <div
                key={conversation._id}
                onClick={() => onSelectConversation(conversation)}
                className="flex cursor-pointer items-center rounded-lg bg-white p-4 shadow hover:bg-gray-50 dark:bg-brand-500"
              >
                <div className="flex w-full items-center gap-4">
                  {/* Student Section */}
                  <div className="flex w-1/2 items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-500 text-xl font-bold text-white">
                      {conversation.student?.profilePicture ? (
                        <img
                          src={conversation.student.profilePicture}
                          alt={conversation.student.name}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <span>
                          {conversation.student?.name?.charAt(0) || '?'}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium dark:text-white">
                          {conversation.student?.name || 'Unknown'}
                        </h3>
                        <span className="rounded bg-green-100 px-2 py-1 text-xs text-green-800">
                          {conversation.studentRole || 'Student'}
                        </span>
                      </div>
                      <p className="max-w-[200px] truncate text-sm text-gray-500">
                        {conversation.lastMessage?.content || 'No messages yet'}
                      </p>
                    </div>
                  </div>

                  <div className="text-gray-400">⟷</div>

                  {/* Staff Section */}
                  <div className="flex w-1/2 items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-purple-500 text-xl font-bold text-white">
                      {conversation.recipientData?.profilePicture ? (
                        <img
                          src={conversation.recipientData.profilePicture}
                          alt={conversation.recipientData.name}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <span>
                          {conversation.recipientData?.name?.charAt(0) || '?'}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium dark:text-white">
                          {conversation.recipientData?.name || 'Unknown'}
                        </h3>
                        <span className="rounded bg-purple-100 px-2 py-1 text-xs text-purple-800">
                          {conversation.recipientRole || 'Staff'}
                        </span>
                      </div>
                      <span className="text-sm text-gray-500">
                        {new Date(conversation.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminChatSystem() {
  const [students, setStudents] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllStudents, setShowAllStudents] = useState(false);
  const [allStudents, setAllStudents] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState(0);
  const [showAllConversations, setShowAllConversations] = useState(false);
  const [allConversations, setAllConversations] = useState([]);
  const [viewingFromAllConversations, setViewingFromAllConversations] =
    useState(false);
  const messagesEndRef = useRef(null);
  const { state } = useContext(AuthContext);

  const adminId = state?.user?.id;
  const adminRole = state?.user?.role;

  useEffect(() => {
    fetchAdminConversations();
    fetchUnreadCounts();
    fetchAllConversationsData();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (currentConversation) {
        fetchConversationWithStudent(currentConversation.student._id);
      } else if (showAllConversations) {
        fetchAllConversationsData();
      } else {
        fetchAdminConversations();
        fetchUnreadCounts();
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [currentConversation, showAllConversations]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchUnreadCounts = async () => {
    try {
      const response = await chatService.getUnreadMessageCounts();
      setUnreadCounts(response.data.data.totalUnread);
    } catch (err) {
      console.error('Error fetching unread counts:', err);
    }
  };

  const fetchAdminConversations = async () => {
    try {
      setLoading(true);
      const response = await chatService.getUserConversations();
      setStudents(response?.data?.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching conversations');
      setLoading(false);
    }
  };

  const fetchAllConversationsData = async () => {
    try {
      setLoading(true);
      const response = await chatService.getAllConversations();
      setAllConversations(response.data.data);
      setLoading(false);
    } catch (err) {
      setError(
        err.response?.data?.message || 'Error fetching all conversations'
      );
      setLoading(false);
    }
  };

  const fetchAllStudents = async () => {
    try {
      setLoading(true);
      const response = await chatService.getAllStudents();
      setAllStudents(response.data.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching students');
      setLoading(false);
    }
  };

  const fetchConversationWithStudent = async (studentId) => {
    try {
      setLoading(true);
      const response = await chatService.getConversationWithStudent(studentId);
      setCurrentConversation(response.data.data);
      setMessages(response.data.data.messages);
      setLoading(false);
      fetchUnreadCounts();
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching conversation');
      setLoading(false);
    }
  };

  const handleStudentSelect = (conversation) => {
    if (showAllConversations) {
      setViewingFromAllConversations(true);
      fetchSpecificConversation(conversation._id);
    } else {
      setViewingFromAllConversations(false);
      fetchConversationWithStudent(conversation.student._id);
    }
  };

  // Add this new function to fetch a specific conversation by ID
  const fetchSpecificConversation = async (conversationId) => {
    try {
      setLoading(true);
      const response = await chatService.getSpecificConversation(
        conversationId
      );
      setCurrentConversation(response.data.data);
      setMessages(response.data.data.messages);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching conversation');
      setLoading(false);
    }
  };

  const startNewConversation = async (studentId) => {
    try {
      setLoading(true);
      const response = await chatService.createOrGetStudentConversation(
        studentId
      );
      setCurrentConversation(response.data.data);
      setMessages(response.data.data.messages || []);
      setShowAllStudents(false);
      setShowAllConversations(false);
      setViewingFromAllConversations(false);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error creating conversation');
      setLoading(false);
    }
  };

  const toggleAllStudents = () => {
    if (!showAllStudents && allStudents.length === 0) {
      fetchAllStudents();
    }
    setShowAllStudents(!showAllStudents);
    setShowAllConversations(false);
    setViewingFromAllConversations(false);
  };

  const toggleAllConversations = () => {
    if (!showAllConversations) {
      fetchAllConversationsData();
    }
    setShowAllConversations(!showAllConversations);
    setShowAllStudents(false);
    setCurrentConversation(null);
    setViewingFromAllConversations(false);
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !currentConversation) return;

    try {
      setLoading(true);
      await chatService.sendMessageToStudent(
        currentConversation._id,
        messageInput.trim()
      );

      fetchConversationWithStudent(currentConversation.student._id);
      setMessageInput('');
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Error sending message');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatLastActive = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    } else if (diffDays === 1) {
      return `Yesterday at ${date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const goBack = () => {
    if (currentConversation) {
      setCurrentConversation(null);
      setMessages([]);

      if (viewingFromAllConversations) {
        setShowAllConversations(true);
        fetchAllConversationsData();
      } else {
        fetchAdminConversations();
      }
    } else {
      setShowAllConversations(false);
      setShowAllStudents(false);
      setViewingFromAllConversations(false);
    }
  };

  const getUnreadCount = (conversation) => {
    return conversation.unreadCount?.recipient || 0;
  };

  const getStudentInitial = (student) => {
    return student?.name?.charAt(0) || '?';
  };

  const isAdminMessage = (msg) => {
    console.log(msg, 'msg');

    if (adminId && msg.senderId === adminId) {
      return true;
    }

    if (msg.senderModel) {
      console.log(msg.senderModel, 'msg.senderModel');

      const senderModelLower = msg.senderModel.toLowerCase();
      if (adminRole && senderModelLower === adminRole.toLowerCase()) {
        return true;
      }
      if (
        senderModelLower.includes('admin') ||
        senderModelLower.includes('superadmin') ||
        senderModelLower.includes('teacher') ||
        senderModelLower.includes('account') ||
        senderModelLower.includes('hr')
      ) {
        return true;
      }
    }

    if (msg.senderRole && msg.senderRole.toLowerCase() === 'admin') {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (adminId && messages.length > 0) {
      setMessages([...messages]);
    }
  }, [adminId]);

  const shouldShowInputField =
    currentConversation && !viewingFromAllConversations;

  return (
    <div className="flex h-screen flex-col bg-gray-100 dark:bg-navy-800">
      {/* Action Buttons */}
      {!currentConversation && state.user.role === 'admin' && (
        <div className="mx-6 mt-4 flex space-x-4">
          <button
            onClick={toggleAllConversations}
            className={`rounded px-4 py-2 text-white ${
              showAllConversations
                ? 'bg-blue-700'
                : 'bg-blue-500 hover:bg-blue-600'
            }`}
          >
            {showAllConversations
              ? 'Hide All Conversations'
              : 'All Conversations'}
          </button>
          {unreadCounts > 0 && (
            <div className="ml-auto flex items-center rounded-full bg-red-500 px-3 py-1 text-sm text-white">
              {unreadCounts} unread
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-grow overflow-y-auto p-6">
        {/* All Conversations View */}
        {showAllConversations && !currentConversation && (
          <AllConversations
            goBack={goBack}
            conversations={allConversations}
            onSelectConversation={handleStudentSelect}
            loading={loading}
          />
        )}

        {/* Default Student List View */}
        {!currentConversation && !showAllStudents && !showAllConversations && (
          <div className="space-y-4">
            <h2 className="mb-4 text-lg font-medium text-gray-700">
              Select a student to chat with
            </h2>
            {loading ? (
              <div className="flex justify-center p-4">
                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {students.map((conversation) => (
                  <div
                    key={conversation._id}
                    onClick={() => handleStudentSelect(conversation)}
                    className="flex cursor-pointer items-center rounded-lg bg-white  p-4 shadow hover:bg-gray-50 dark:bg-brand-500"
                  >
                    <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-500 text-xl font-bold text-white">
                      {conversation.student.profilePicture ? (
                        <img
                          src={conversation.student.profilePicture}
                          alt={conversation.student.name}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <span>{getStudentInitial(conversation.student)}</span>
                      )}
                    </div>
                    <div className="flex-grow">
                      <div className="flex items-center justify-between">
                        <h3 className="font-medium dark:text-white">
                          {conversation.student.name}
                        </h3>
                        <span className="text-sm text-gray-500">
                          {formatLastActive(conversation.updatedAt)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="truncate text-sm text-gray-500">
                          {conversation.lastMessage?.content ||
                            'No messages yet'}
                        </p>
                        {getUnreadCount(conversation) > 0 && (
                          <span className="ml-2 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-xs text-white">
                            {getUnreadCount(conversation)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {students.length === 0 && !loading && (
                  <div className="py-8 text-center text-gray-500">
                    No student conversations found
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* All Students View */}
        {!currentConversation && showAllStudents && (
          <div className="space-y-4">
            <h2 className="mb-4 text-lg font-medium text-gray-700">
              Select a student to start a new conversation
            </h2>
            {loading ? (
              <div className="flex justify-center p-4">
                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {allStudents.map((student) => (
                  <div
                    key={student._id}
                    onClick={() => startNewConversation(student._id)}
                    className="flex cursor-pointer items-center rounded-lg bg-white p-4 shadow hover:bg-gray-50"
                  >
                    <div className="mr-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-300 text-xl font-bold text-white">
                      {student.profilePicture ? (
                        <img
                          src={student.profilePicture}
                          alt={student.name}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <span>{getStudentInitial(student)}</span>
                      )}
                    </div>
                    <div className="flex-grow">
                      <h3 className="font-medium">{student.name}</h3>
                      <p className="text-sm text-gray-500">{student.email}</p>
                    </div>
                  </div>
                ))}

                {allStudents.length === 0 && !loading && (
                  <div className="py-8 text-center text-gray-500">
                    No students found
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Current Conversation View */}
        {currentConversation && (
          <div className="flex h-full flex-col">
            {/* Chat header */}
            <div className="mb-4 flex items-center">
              <button
                onClick={goBack}
                className="mr-4 text-gray-600 hover:text-gray-800"
              >
                ← Back
              </button>
              <h1 className="text-xl font-bold text-gray-800 dark:text-white">
                Chat with {currentConversation.student.name}
              </h1>
              {viewingFromAllConversations && (
                <span className="ml-4 rounded bg-blue-100 px-3 py-1 text-xs text-blue-800">
                  Viewing from All Conversations
                </span>
              )}
            </div>

            {/* Chat messages */}
            <div className="mb-4 flex-grow space-y-4 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="rounded-lg border bg-gray-200 p-3 text-center text-gray-500 dark:bg-navy-800">
                  No messages yet. Start the conversation!
                </div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg._id}
                    className={`flex ${
                      isAdminMessage(msg) ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-xs rounded-lg p-3 md:max-w-md ${
                        isAdminMessage(msg)
                          ? 'rounded-br-none bg-green-500 text-white'
                          : 'rounded-bl-none border bg-white text-gray-800  dark:bg-gray-900 dark:text-white'
                      }`}
                    >
                      {!isAdminMessage(msg) && (
                        <div className="mb-1 text-sm font-medium text-gray-500">
                          {currentConversation.student.name}
                        </div>
                      )}
                      <p>{msg.content}</p>
                      <div
                        className={`mt-1 text-xs ${
                          isAdminMessage(msg)
                            ? 'text-green-100'
                            : 'text-gray-400'
                        }`}
                      >
                        {formatDate(msg.createdAt)}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
        )}
      </div>

      {/* Chat input (only visible when a student is selected AND not viewing from all conversations) */}
      {shouldShowInputField && (
        <div className="border-t bg-white  p-4 dark:bg-navy-800">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              sendMessage();
            }}
            className="flex items-center"
          >
            <input
              type="text"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              placeholder="Type your message..."
              className="flex-grow rounded-l-lg border p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-navy-900 dark:text-white"
            />
            <button
              type="submit"
              disabled={loading || !messageInput.trim()}
              className={`${
                loading || !messageInput.trim()
                  ? 'cursor-not-allowed bg-green-300'
                  : 'bg-green-500 hover:bg-green-600'
              } rounded-r-lg p-2 text-white`}
            >
              {loading ? '...' : 'Send'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
